import { format, isValid, parse } from 'date-fns'
import { es } from 'date-fns/locale'
import { Eraser, X } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { EconomicCycleBadge } from '@/containers/billing/register/billingFilters/filter/advanced/EconomicCycleBadge'
import {
  areaSalesToOptions,
  paymentGatewaysToOptions,
} from '@/containers/billing/register/billingFilters/hooks/useFilterOptionsData'
import { useBusinessDataQuery } from '@/hooks/business/useBusinessDataQuery'

import { usePaymentGatewaysQuery } from '@/hooks/business/usePaymentGatewaysQuery'

import { codeCurrencyOptions, statusOptions } from './constants/statusOptions'
import { useCurrencyPaymentsFilters } from '../hooks/useCurrencyPaymentsFilters'
import { useAreasQuery } from '@/hooks/business/useAreasQuery'

// Date format for URL (YYYY-MM-DD)
const URL_DATE_FORMAT = 'yyyy-MM-dd'

// Helper function to format date for display
const formatDateForDisplay = (dateString: string | null | undefined) => {
  if (!dateString) return null
  try {
    const date = parse(dateString, URL_DATE_FORMAT, new Date())
    if (!isValid(date)) return null
    return format(date, 'd MMM yyyy', { locale: es })
  } catch {
    return null
  }
}

export const SelectionSummary = () => {
  const {
    filters,
    selectedStatus,
    clearAmountFilter,
    clearStatusFilter,
    clearEconomicCycleIdsFilter,
    clearPaymentGatewayIdsFilter,
    clearCodeCurrencyFilter,
    clearDateRangeFilter,
    clearAreaSalesIdFilter,
    clearAllFilters,
  } = useCurrencyPaymentsFilters()

  // Fetch business data and related information
  const { businessData, isLoadingBusinessData } = useBusinessDataQuery({})

  const { areas: areaSales = [] } = useAreasQuery({
    params: {
      businessId: businessData?.id ?? 0,
    },
    options: {
      enabled: !!businessData?.id && !isLoadingBusinessData,
    },
  })

  const areaSalesOptions = areaSalesToOptions(areaSales)

  const { paymentGateways = [] } = usePaymentGatewaysQuery({
    businessId: businessData?.id ?? 0,
    options: {
      enabled: !!businessData?.id && !isLoadingBusinessData,
    },
  })

  const paymentGatewayOptions = paymentGatewaysToOptions(paymentGateways)

  const findLabelById = (
    options: { value: string; label: string }[],
    id: string
  ) => {
    return options.find((option) => option.value === id)?.label || id
  }

  // Helper functions to find labels by value
  const findLabelByValue = (
    options: { value: string; label: string }[],
    value: string
  ) => {
    return options.find((option) => option.value === value)?.label || value
  }

  const getPaymentGatewayLabel = (gateway: string) => {
    const label = findLabelById(paymentGatewayOptions, gateway)
    return label !== gateway ? label : gateway
  }

  const getAreaSalesLabel = (areaId: string) => {
    const label = findLabelById(areaSalesOptions, areaId)
    return label !== areaId ? label : areaId
  }

  // Format date range for display
  const getDateRangeDisplay = () => {
    const fromFormatted = formatDateForDisplay(filters.dateFrom)
    const toFormatted = formatDateForDisplay(filters.dateTo)

    if (fromFormatted && toFormatted) {
      return `${fromFormatted} - ${toFormatted}`
    } else if (fromFormatted) {
      return `Desde el ${fromFormatted}`
    } else if (toFormatted) {
      return `Hasta el ${toFormatted}`
    }
    return null
  }

  // Format amount range for display
  const getAmountRangeDisplay = () => {
    if (!filters.amountFrom && !filters.amountTo) return null

    const currencyLabel = findLabelByValue(
      codeCurrencyOptions,
      filters.codeCurrency || 'CUP'
    )

    if (filters.amountFrom && filters.amountTo) {
      return `${filters.amountFrom} - ${filters.amountTo} ${currencyLabel}`
    } else if (filters.amountFrom) {
      return `≥ ${filters.amountFrom} ${currencyLabel}`
    } else if (filters.amountTo) {
      return `≤ ${filters.amountTo} ${currencyLabel}`
    }
    return null
  }

  const dateRangeDisplay = getDateRangeDisplay()
  const amountRangeDisplay = getAmountRangeDisplay()

  return (
    <div className="flex flex-wrap items-center gap-4">
      {selectedStatus && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">Estado:</span>
            <Badge
              className="flex items-center gap-1 bg-gray-200 text-gray-800 hover:bg-gray-300"
              key={selectedStatus}
              variant="default"
            >
              <span>
                {statusOptions.find((s) => s.value === selectedStatus)?.label ||
                  selectedStatus}
              </span>
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => clearStatusFilter()}
              />
            </Badge>
          </div>
          <Separator className="h-4" orientation="vertical" />
        </>
      )}
      {/* Date Range */}
      {dateRangeDisplay && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">Fecha:</span>
            <Badge
              className="flex items-center gap-1 bg-gray-200 text-gray-800 hover:bg-gray-300"
              variant="default"
            >
              <span>{dateRangeDisplay}</span>
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={clearDateRangeFilter}
              />
            </Badge>
          </div>
          <Separator className="h-4" orientation="vertical" />
        </>
      )}

      {/* Amount Range */}
      {amountRangeDisplay && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">Importe:</span>
            <Badge
              className="flex items-center gap-1 bg-gray-200 text-gray-800 hover:bg-gray-300"
              variant="default"
            >
              <span>{amountRangeDisplay}</span>
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => {
                  clearAmountFilter()
                  clearCodeCurrencyFilter()
                }}
              />
            </Badge>
          </div>
          <Separator className="h-4" orientation="vertical" />
        </>
      )}

      {filters.areaSalesId && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">Punto de venta:</span>
            <Badge
              className="flex items-center gap-1 bg-gray-200 text-gray-800 hover:bg-gray-300"
              key={filters.areaSalesId}
              variant="default"
            >
              <span>{getAreaSalesLabel(filters.areaSalesId)}</span>
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => clearAreaSalesIdFilter()}
              />
            </Badge>
          </div>
          <Separator className="h-4" orientation="vertical" />
        </>
      )}

      {filters.economicCycleIds && filters.economicCycleIds.length > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">Ciclos económicos:</span>
            {filters.economicCycleIds.map((cycleId) => (
              <EconomicCycleBadge
                cycleId={cycleId}
                key={cycleId}
                onRemove={() => clearEconomicCycleIdsFilter(cycleId)}
              />
            ))}
          </div>
          <Separator className="h-4" orientation="vertical" />
        </>
      )}

      {filters.paymentGatewayIds && filters.paymentGatewayIds.length > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">Métodos de pago:</span>
            {filters.paymentGatewayIds.map((gatewayId) => (
              <Badge
                className="flex items-center gap-1 bg-gray-200 text-gray-800 hover:bg-gray-300"
                key={gatewayId}
                variant="default"
              >
                <span>{getPaymentGatewayLabel(gatewayId)}</span>
                <X
                  className="h-3 w-3 cursor-pointer"
                  onClick={() => clearPaymentGatewayIdsFilter(gatewayId)}
                />
              </Badge>
            ))}
          </div>
          <Separator className="h-4" orientation="vertical" />
        </>
      )}

      {/* Clear All Button */}
      <Button
        className="flex items-center gap-2 text-muted-foreground transition-colors hover:text-destructive"
        onClick={clearAllFilters}
        size="sm"
        variant="ghost"
      >
        <Eraser className="h-4 w-4" />
        <span>Limpiar todo</span>
      </Button>
    </div>
  )
}
